/**
 * Device Preview Store
 * Generic store for preview UI state (zoom, grid, measurements)
 * Device-agnostic - works for kiosk, charging station, etc.
 */

import { create } from 'zustand';

/**
 * Preview UI state
 */
interface DevicePreviewState {
  // Zoom/scale level (1 = 100%, 0.5 = 50%, 2 = 200%)
  scale: number;

  // Whether to show grid overlay
  showGrid: boolean;

  // Whether to show measurement guides
  showMeasurements: boolean;

  // Whether to show debug info
  showDebug: boolean;
}

/**
 * Preview UI actions
 */
interface DevicePreviewActions {
  // Set zoom scale
  setScale: (scale: number) => void;

  // Toggle grid overlay
  toggleGrid: () => void;

  // Toggle measurement guides
  toggleMeasurements: () => void;

  // Toggle debug mode
  toggleDebug: () => void;

  // Reset to defaults
  reset: () => void;
}

/**
 * Complete preview store type
 */
type DevicePreviewStore = DevicePreviewState & DevicePreviewActions;

/**
 * Default preview state
 */
const defaultState: DevicePreviewState = {
  scale: 1,
  showGrid: false,
  showMeasurements: false,
  showDebug: false,
};

/**
 * Device Preview Store
 * Manages preview UI state for device customization
 */
export const useDevicePreviewStore = create<DevicePreviewStore>((set) => ({
  ...defaultState,

  setScale: (scale) => {
    // Clamp scale between 0.25 (25%) and 2 (200%)
    const clampedScale = Math.max(0.25, Math.min(2, scale));
    set({ scale: clampedScale });
  },

  toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),

  toggleMeasurements: () => set((state) => ({ showMeasurements: !state.showMeasurements })),

  toggleDebug: () => set((state) => ({ showDebug: !state.showDebug })),

  reset: () => set(defaultState),
}));

/**
 * Zoom presets for quick access
 */
export const ZOOM_PRESETS = {
  FIT: 0.5, // 50% - Fit large screens
  NORMAL: 1, // 100% - Actual size
  LARGE: 1.5, // 150% - Zoomed in
  XLARGE: 2, // 200% - Maximum zoom
} as const;

/**
 * Helper hook for zoom controls
 */
export function useZoomControls() {
  const { scale, setScale } = useDevicePreviewStore();

  const zoomIn = () => setScale(scale + 0.25);
  const zoomOut = () => setScale(scale - 0.25);
  const resetZoom = () => setScale(ZOOM_PRESETS.NORMAL);
  const fitToScreen = () => setScale(ZOOM_PRESETS.FIT);

  return {
    scale,
    zoomIn,
    zoomOut,
    resetZoom,
    fitToScreen,
    canZoomIn: scale < ZOOM_PRESETS.XLARGE,
    canZoomOut: scale > 0.25,
  };
}
